import { useEffect, useState, useRef, useCallback } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Link } from "react-router-dom";
import axios from "axios";
import { Home, Hammer, TrendingUp, Handshake, CheckCircle, Wallet, Sparkles, ArrowRight, Phone, Mail, MapPin, Leaf, Instagram, Linkedin } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Custom hook for animated counters
const useCounter = (end, duration = 2000, startOnView = true) => {
  const [count, setCount] = useState(0);
  const [hasStarted, setHasStarted] = useState(false);
  const ref = useRef(null);

  const startAnimation = useCallback(() => {
    if (hasStarted) return;
    setHasStarted(true);
    
    const endNum = parseInt(end.toString().replace(/[^0-9]/g, ''));
    const startTime = Date.now();
    
    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const easeOut = 1 - Math.pow(1 - progress, 3);
      setCount(Math.floor(endNum * easeOut));
      
      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };
    
    requestAnimationFrame(animate);
  }, [end, duration, hasStarted]);

  useEffect(() => {
    if (!startOnView) {
      startAnimation();
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          startAnimation();
        }
      },
      { threshold: 0.3 }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => observer.disconnect();
  }, [startAnimation, startOnView]);

  return { count, ref };
};

// Hook for scroll reveal
const useScrollReveal = () => {
  useEffect(() => {
    const reveals = document.querySelectorAll('.reveal, .reveal-stagger');
    
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('active');
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -50px 0px' }
    );

    reveals.forEach(el => observer.observe(el));

    return () => observer.disconnect();
  }, []);
};

// Hero Section
const HeroSection = () => {
  const scrollToForm = () => {
    document.getElementById('contact-form')?.scrollIntoView({ behavior: 'smooth' });
  };

  const scrollToHowItWorks = () => {
    document.getElementById('value-creation')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section className="min-h-screen relative overflow-hidden bg-gradient-to-br from-white via-gray-50 to-gray-100 flex items-center" data-testid="hero-section">
      {/* Subtle pattern overlay */}
      <div className="absolute inset-0 opacity-[0.03]" style={{
        backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23000000' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
      }} />
      
      <div className="container-flippit relative z-10">
        <div className="max-w-4xl mx-auto text-center py-16 lg:py-0">
          {/* Badge */}
          <div className="reveal mb-8">
            <div className="trust-badge inline-flex">
              <Sparkles size={16} />
              <span>Servizio esclusivo per Milano</span>
            </div>
          </div>
          
          {/* Main Title */}
          <div className="reveal mb-8">
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-black text-flippit-dark leading-tight tracking-tight" style={{ fontFamily: 'Nunito, sans-serif' }}>
              Non svenderla.
            </h1>
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-black leading-tight tracking-tight mt-2" style={{ fontFamily: 'Nunito, sans-serif' }}>
              <span className="text-[#E6007E]">
                Flipp
                <span className="inline-block animate-bounce-rotate">!</span>
                t.
              </span>
            </h1>
          </div>
          
          {/* Subtitle */}
          <p className="reveal text-lg md:text-xl text-flippit-gray mb-10 max-w-2xl mx-auto leading-relaxed">
            Ristrutturiamo il tuo immobile a Milano senza chiederti anticipi. 
            Lo vendiamo valorizzato e dividiamo il guadagno.
          </p>
          
          {/* CTA Buttons */}
          <div className="reveal flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              onClick={scrollToForm}
              className="btn-magenta px-8 py-6 text-base font-semibold rounded-full"
              data-testid="cta-valuta-btn"
            >
              Valuta il tuo immobile
              <ArrowRight className="ml-2" size={18} />
            </Button>
            <Button 
              onClick={scrollToHowItWorks}
              variant="outline"
              className="px-8 py-6 text-base font-semibold rounded-full border-2 border-gray-900 hover:bg-gray-900 hover:text-white transition-all"
              data-testid="cta-scopri-btn"
            >
              Scopri come funziona
            </Button>
          </div>

          {/* Trust indicators */}
          <div className="reveal mt-12 flex flex-wrap items-center justify-center gap-6 text-sm text-flippit-gray">
            <div className="flex items-center gap-2">
              <CheckCircle size={18} className="text-green-500" />
              <span>Zero anticipi</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle size={18} className="text-green-500" />
              <span>Solo 1% di provvigione sulla vendita</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle size={18} className="text-green-500" />
              <span>Gestione completa</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Target Audience Section
const TargetAudienceSection = () => {
  return (
    <section className="py-12 md:py-16 bg-white" data-testid="target-audience-section">
      <div className="container-flippit">
        <div className="max-w-4xl mx-auto text-center reveal">
          <h2 className="heading-lg text-flippit-dark mb-8">Flippit è per te se...</h2>
          
          <div className="grid md:grid-cols-2 gap-6 text-left">
            <div className="p-6 bg-gray-50 rounded-2xl border-l-4 border-[#E6007E]">
              <p className="text-lg text-flippit-dark leading-relaxed">
                <span className="font-semibold">Hai un immobile da vendere</span> e vuoi massimizzare i tuoi guadagni senza investire nella ristrutturazione.
              </p>
            </div>
            
            <div className="p-6 bg-gray-50 rounded-2xl border-l-4 border-[#E6007E]">
              <p className="text-lg text-flippit-dark leading-relaxed">
                <span className="font-semibold">Sei un investitore</span> stanco di anticipare le spese di ristrutturazione e gestire i cantieri.
              </p>
            </div>
            
            <div className="p-6 bg-gray-50 rounded-2xl border-l-4 border-[#E6007E]">
              <p className="text-lg text-flippit-dark leading-relaxed">
                <span className="font-semibold">Hai ereditato un appartamento</span> datato e non sai come valorizzarlo al meglio.
              </p>
            </div>
            
            <div className="p-6 bg-gray-50 rounded-2xl border-l-4 border-[#E6007E]">
              <p className="text-lg text-flippit-dark leading-relaxed">
                <span className="font-semibold">Non hai tempo o competenze</span> per seguire lavori di ristrutturazione e vendita.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Value Creation Section (replaces Before/After)
const ValueCreationSection = () => {
  const scrollToForm = () => {
    document.getElementById('contact-form')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section className="py-12 md:py-16 bg-white" id="value-creation" data-testid="value-creation-section">
      <div className="container-flippit">
        {/* Header */}
        <div className="max-w-3xl mb-10 reveal">
          <h2 className="heading-lg text-flippit-dark mb-4">Come creiamo valore insieme</h2>
          <p className="text-lg text-flippit-gray leading-relaxed">
            Flippit valorizza il tuo immobile prima della vendita. 
            Ti mostriamo quanto vale oggi, quanto costerà la ristrutturazione e quanto potrà valere una volta rinnovato.
          </p>
        </div>

        {/* Steps */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12 reveal-stagger">
          
          {/* Step 1 */}
          <div className="p-6 bg-gray-50 rounded-xl">
            <span className="flex-shrink-0 w-10 h-10 rounded-full bg-[#E6007E] text-white flex items-center justify-center font-bold text-lg mb-4">1</span>
            <h3 className="text-lg font-bold text-flippit-dark mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>Valutazione dell'immobile</h3>
            <p className="text-sm text-flippit-gray leading-relaxed">Analizziamo il tuo immobile e stimiamo il valore di mercato nello stato attuale.</p>
          </div>

          {/* Step 2 */}
          <div className="p-6 bg-gray-50 rounded-xl">
            <span className="flex-shrink-0 w-10 h-10 rounded-full bg-[#E6007E] text-white flex items-center justify-center font-bold text-lg mb-4">2</span>
            <h3 className="text-lg font-bold text-flippit-dark mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>Stima della ristrutturazione</h3>
            <p className="text-sm text-flippit-gray leading-relaxed">Il nostro team di architetti definisce gli interventi necessari e stima il costo dei lavori.</p>
          </div>

          {/* Step 3 */}
          <div className="p-6 bg-gray-50 rounded-xl">
            <span className="flex-shrink-0 w-10 h-10 rounded-full bg-[#E6007E] text-white flex items-center justify-center font-bold text-lg mb-4">3</span>
            <h3 className="text-lg font-bold text-flippit-dark mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>Valore post ristrutturazione</h3>
            <p className="text-sm text-flippit-gray leading-relaxed">Stimiamo il nuovo valore di mercato grazie alla ristrutturazione e alla strategia di vendita.</p>
          </div>

          {/* Step 4 */}
          <div className="p-6 bg-gray-50 rounded-xl">
            <span className="flex-shrink-0 w-10 h-10 rounded-full bg-[#E6007E] text-white flex items-center justify-center font-bold text-lg mb-4">4</span>
            <h3 className="text-lg font-bold text-flippit-dark mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>Ristrutturazione e vendita</h3>
            <p className="text-sm text-flippit-gray leading-relaxed">Flippit anticipa i costi della ristrutturazione, gestisce i lavori e mette l'immobile sul mercato.</p>
          </div>

          {/* Step 5 */}
          <div className="p-6 bg-gray-50 rounded-xl">
            <span className="flex-shrink-0 w-10 h-10 rounded-full bg-[#E6007E] text-white flex items-center justify-center font-bold text-lg mb-4">5</span>
            <h3 className="text-lg font-bold text-flippit-dark mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>Divisione del valore creato</h3>
            <p className="text-sm text-flippit-gray leading-relaxed">Alla vendita vengono saldati i costi della ristrutturazione e viene divisa la plusvalenza generata.</p>
          </div>

        </div>

        {/* Final Calculation Box */}
        <div className="reveal">
          <div className="bg-gray-100 p-6 md:p-8 rounded-2xl border border-gray-200">
            <p className="text-xs uppercase tracking-wider text-flippit-gray mb-4">Esempio di calcolo</p>
            
            <div className="grid md:grid-cols-2 gap-8">
              {/* Left - Numbers */}
              <div className="space-y-3">
                <div className="flex justify-between items-center pb-3 border-b border-gray-200">
                  <span className="text-flippit-gray">Prezzo vendita</span>
                  <span className="text-lg font-semibold text-flippit-dark">430.000 €</span>
                </div>
                <div className="flex justify-between items-center pb-3 border-b border-gray-200">
                  <span className="text-flippit-gray">Valore iniziale immobile</span>
                  <span className="text-lg font-semibold text-flippit-dark">- 350.000 €</span>
                </div>
                <div className="flex justify-between items-center pb-3 border-b border-gray-200">
                  <span className="text-flippit-gray">Costo ristrutturazione</span>
                  <span className="text-lg font-semibold text-flippit-dark">- 35.000 €</span>
                </div>
                <div className="flex justify-between items-center pt-2">
                  <span className="text-flippit-dark font-semibold">Valore creato</span>
                  <span className="text-2xl font-bold text-[#E6007E]">45.000 €</span>
                </div>
              </div>

              {/* Right - Split */}
              <div className="bg-white rounded-xl p-5 border border-gray-200">
                <p className="text-sm text-flippit-gray mb-4">Divisione del profitto</p>
                <div className="grid grid-cols-2 gap-4">
                  <div className="text-center p-4 bg-gray-50 rounded-lg">
                    <p className="text-3xl font-bold text-[#E6007E]" style={{ fontFamily: 'Manrope, sans-serif' }}>50%</p>
                    <p className="text-sm text-flippit-gray mt-1">Flippit</p>
                  </div>
                  <div className="text-center p-4 bg-gray-50 rounded-lg">
                    <p className="text-3xl font-bold text-flippit-dark" style={{ fontFamily: 'Manrope, sans-serif' }}>50%</p>
                    <p className="text-sm text-flippit-gray mt-1">Proprietario</p>
                  </div>
                </div>
                <p className="text-sm text-flippit-gray mt-4 leading-relaxed">
                  Il proprietario ottiene un guadagno extra senza aver anticipato i costi della ristrutturazione.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="mt-10 text-center reveal">
          <h3 className="heading-md text-flippit-dark mb-4">Scopri quanto valore può generare il tuo immobile</h3>
          <Button 
            onClick={scrollToForm}
            className="btn-magenta px-8 py-6 text-base font-semibold rounded-full"
            data-testid="value-cta-btn"
          >
            Richiedi valutazione gratuita
            <ArrowRight className="ml-2" size={18} />
          </Button>
        </div>

      </div>
    </section>
  );
};

// How It Works Section
const HowItWorksSection = () => {
  const steps = [
    {
      num: "01",
      icon: Home,
      title: "Valutazione immobile",
      desc: "Analizziamo il valore reale dell'immobile con un agente immobiliare specializzato su Milano."
    },
    {
      num: "02",
      icon: Hammer,
      title: "Progetto di valorizzazione",
      desc: "Il nostro team di architetti definisce il progetto e il budget di ristrutturazione."
    },
    {
      num: "03",
      icon: TrendingUp,
      title: "Ristrutturazione",
      desc: "Flippit anticipa i costi e gestisce interamente il cantiere."
    },
    {
      num: "04",
      icon: Handshake,
      title: "Vendita",
      desc: "L'immobile viene venduto ristrutturato e il profitto generato viene condiviso."
    }
  ];

  return (
    <section id="how-it-works" className="py-12 md:py-16 bg-flippit-light" data-testid="how-it-works-section">
      <div className="container-flippit">
        <div className="text-center mb-10 reveal">
          <h2 className="heading-lg text-flippit-dark mb-4">Come funziona</h2>
          <p className="text-flippit-gray max-w-2xl mx-auto">
            Un processo semplice e trasparente per massimizzare il valore del tuo immobile
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-6 reveal-stagger">
          {steps.map((step) => (
            <div 
              key={step.num} 
              className="process-card card-hover"
              data-testid={`step-${step.num}`}
            >
              <span className="step-number">{step.num}</span>
              <div className="relative z-10">
                <div className="benefit-icon mb-4">
                  <step.icon size={24} />
                </div>
                <h3 className="heading-md text-flippit-dark mb-3">{step.title}</h3>
                <p className="text-flippit-gray leading-relaxed">{step.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Market Numbers Section with Animated Counters
const MarketNumbersSection = () => {
  const stats = [
    { value: "25000", suffix: "+", label: "Compravendite/anno" },
    { value: "5600", suffix: "€", label: "Prezzo medio mq" },
    { value: "30", suffix: "%", label: "Aumento valore" },
    { value: "0", suffix: "€", label: "Anticipo richiesto", highlight: true }
  ];

  return (
    <section className="py-10 md:py-12 bg-white" data-testid="market-numbers-section">
      <div className="container-flippit">
        <div className="text-center mb-6 reveal">
          <p className="text-sm text-flippit-gray uppercase tracking-wider mb-1">Milano è il mercato ideale</p>
        </div>

        <div className="flex flex-wrap justify-center gap-8 md:gap-12 reveal">
          {stats.map((stat, index) => (
            <StatCard key={index} {...stat} />
          ))}
        </div>
      </div>
    </section>
  );
};

const StatCard = ({ value, suffix, label, highlight }) => {
  const { count, ref } = useCounter(value, 2000);
  
  return (
    <div 
      ref={ref}
      className="text-center"
      data-testid={`stat-${value}`}
    >
      <p className={`text-2xl md:text-3xl font-bold ${highlight ? 'text-[#E6007E]' : 'text-flippit-dark'}`} style={{ fontFamily: 'Manrope, sans-serif' }}>
        {count}{suffix}
      </p>
      <p className="text-xs text-flippit-gray mt-1">{label}</p>
    </div>
  );
};

// Benefits Section
const BenefitsSection = () => {
  const benefits = [
    {
      icon: Wallet,
      title: "Nessun anticipo",
      desc: "Flippit anticipa i costi della ristrutturazione. Tu non tiri fuori un euro.",
      featured: false
    },
    {
      icon: TrendingUp,
      title: "Più valore",
      desc: "Un immobile ristrutturato si vende molto meglio sul mercato milanese.",
      featured: false
    },
    {
      icon: CheckCircle,
      title: "Gestione completa",
      desc: "Architettura, lavori e vendita gestiti da un unico team specializzato.",
      featured: false
    },
    {
      icon: Sparkles,
      title: "Solo 1% di provvigione",
      desc: "Con Flippit le provvigioni di vendita sono solo dell'1% per il venditore.",
      featured: true
    },
    {
      icon: Home,
      title: "Professionisti abilitati",
      desc: "Sei sempre seguito da architetti e agenti immobiliari iscritti agli albi professionali.",
      featured: false
    }
  ];

  return (
    <section className="py-12 md:py-16 bg-flippit-light" data-testid="benefits-section">
      <div className="container-flippit">
        <div className="text-center mb-10 reveal">
          <h2 className="heading-lg text-flippit-dark mb-4">Perché usare Flippit</h2>
          <p className="text-flippit-gray max-w-2xl mx-auto">
            Massimizza il valore del tuo immobile senza stress e senza anticipi
          </p>
        </div>

        <div className="grid md:grid-cols-3 lg:grid-cols-5 gap-4 reveal-stagger">
          {benefits.map((benefit, index) => (
            <div 
              key={index}
              className={`p-5 rounded-2xl card-hover ${benefit.featured ? 'bg-[#E6007E] text-white' : 'bg-white border border-gray-100'}`}
              data-testid={`benefit-${index}`}
            >
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center mb-4 ${benefit.featured ? 'bg-white/20' : 'bg-pink-50'}`}>
                <benefit.icon size={20} className={benefit.featured ? 'text-white' : 'text-[#E6007E]'} />
              </div>
              <h3 className={`text-base font-bold mb-2 ${benefit.featured ? 'text-white' : 'text-flippit-dark'}`} style={{ fontFamily: 'Manrope, sans-serif' }}>{benefit.title}</h3>
              <p className={`text-sm leading-relaxed ${benefit.featured ? 'text-white/90' : 'text-flippit-gray'}`}>{benefit.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Green Section
const GreenSection = () => {
  return (
    <section className="py-12 md:py-16 bg-gradient-to-br from-green-50 to-emerald-100" data-testid="green-section">
      <div className="container-flippit">
        <div className="max-w-4xl mx-auto reveal">
          <div className="text-center">
            <div className="inline-flex w-20 h-20 bg-green-500 rounded-2xl items-center justify-center shadow-lg shadow-green-200 mb-6">
              <Leaf className="text-white" size={40} />
            </div>
            <h2 className="heading-lg text-flippit-dark mb-4">Pronti per il futuro green</h2>
            <p className="text-lg text-flippit-gray leading-relaxed max-w-2xl mx-auto">
              Tutte le nostre ristrutturazioni portano il tuo immobile almeno in <span className="font-bold text-green-600">classe energetica D</span>, 
              in previsione della <span className="font-semibold text-flippit-dark">direttiva europea EPBD 2024/1275</span> sugli standard minimi di efficienza energetica.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

// Contact Form Section
const ContactFormSection = () => {
  const [formData, setFormData] = useState({
    nome: '',
    email: '',
    telefono: '',
    zona: '',
    mq: ''
  });
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/leads`, formData);
      toast.success("Richiesta inviata con successo! Ti contatteremo presto.");
      setFormData({ nome: '', email: '', telefono: '', zona: '', mq: '' });
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error("Errore nell'invio. Riprova più tardi.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact-form" className="py-12 md:py-16 form-bg-pattern" data-testid="contact-form-section">
      <div className="container-flippit">
        <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          {/* Left Content */}
          <div className="reveal">
            <h2 className="heading-lg text-flippit-dark mb-6">
              Scopri quanto può valere il tuo immobile ristrutturato.
            </h2>
            <p className="text-flippit-gray text-lg mb-8 leading-relaxed">
              Compila il form per ricevere una valutazione gratuita e senza impegno. 
              Un nostro esperto ti contatterà entro 24 ore.
            </p>
            
            {/* Trust signals */}
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <CheckCircle className="text-green-500" size={20} />
                <span className="text-flippit-dark">Valutazione gratuita e senza impegno</span>
              </div>
              <div className="flex items-center gap-3">
                <CheckCircle className="text-green-500" size={20} />
                <span className="text-flippit-dark">Risposta entro 24 ore</span>
              </div>
              <div className="flex items-center gap-3">
                <CheckCircle className="text-green-500" size={20} />
                <span className="text-flippit-dark">Team specializzato su Milano</span>
              </div>
            </div>

            {/* Contact info */}
            <div className="mt-10 pt-10 border-t border-gray-200">
              <div className="flex flex-col sm:flex-row gap-4">
                <a href="tel:+390212345678" className="flex items-center gap-2 text-flippit-dark hover:text-flippit-magenta transition-colors">
                  <Phone size={18} />
                  <span>+39 02 1234 5678</span>
                </a>
                <a href="mailto:info@flippit.it" className="flex items-center gap-2 text-flippit-dark hover:text-flippit-magenta transition-colors">
                  <Mail size={18} />
                  <span>info@flippit.it</span>
                </a>
              </div>
            </div>
          </div>

          {/* Form */}
          <div className="reveal">
            <form 
              onSubmit={handleSubmit}
              className="bg-white p-8 md:p-10 rounded-2xl shadow-xl"
              data-testid="lead-form"
            >
              <div className="space-y-6">
                <div>
                  <Label htmlFor="nome" className="text-flippit-dark font-medium">Nome</Label>
                  <Input
                    id="nome"
                    name="nome"
                    type="text"
                    value={formData.nome}
                    onChange={handleChange}
                    required
                    className="form-input-modern mt-2"
                    placeholder="Il tuo nome"
                    data-testid="input-nome"
                  />
                </div>

                <div>
                  <Label htmlFor="email" className="text-flippit-dark font-medium">Email</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="form-input-modern mt-2"
                    placeholder="La tua email"
                    data-testid="input-email"
                  />
                </div>

                <div>
                  <Label htmlFor="telefono" className="text-flippit-dark font-medium">Telefono</Label>
                  <Input
                    id="telefono"
                    name="telefono"
                    type="tel"
                    value={formData.telefono}
                    onChange={handleChange}
                    required
                    className="form-input-modern mt-2"
                    placeholder="+39 333 123 4567"
                    data-testid="input-telefono"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="zona" className="text-flippit-dark font-medium">Zona immobile</Label>
                    <Input
                      id="zona"
                      name="zona"
                      type="text"
                      value={formData.zona}
                      onChange={handleChange}
                      required
                      className="form-input-modern mt-2"
                      placeholder="Es. Navigli"
                      data-testid="input-zona"
                    />
                  </div>
                  <div>
                    <Label htmlFor="mq" className="text-flippit-dark font-medium">Mq immobile</Label>
                    <Input
                      id="mq"
                      name="mq"
                      type="text"
                      value={formData.mq}
                      onChange={handleChange}
                      required
                      className="form-input-modern mt-2"
                      placeholder="Es. 80"
                      data-testid="input-mq"
                    />
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full btn-magenta py-6 text-base font-semibold rounded-full mt-4"
                  data-testid="submit-form-btn"
                >
                  {loading ? 'Invio in corso...' : 'Richiedi valutazione gratuita'}
                </Button>

                <p className="text-xs text-center text-flippit-gray mt-4">
                  Inviando il form accetti la nostra{' '}
                  <Link to="/privacy" className="text-flippit-magenta hover:underline">
                    Privacy Policy
                  </Link>
                </p>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

// Footer
const Footer = () => {
  return (
    <footer className="bg-flippit-dark py-12" data-testid="footer">
      <div className="container-flippit">
        <div className="grid md:grid-cols-3 gap-8 items-start">
          {/* Logo */}
          <div>
            <p className="text-gray-400 text-sm mb-1">Non svenderla.</p>
            <h3 className="text-3xl font-bold text-white" style={{ fontFamily: 'Nunito, sans-serif' }}>
              Flipp!t
            </h3>
          </div>

          {/* Contatti */}
          <div>
            <h4 className="text-white font-semibold mb-3">Contatti</h4>
            <div className="space-y-2 text-sm">
              <a href="mailto:info@flippit.it" className="flex items-center gap-2 footer-link">
                <Mail size={16} />
                info@flippit.it
              </a>
              <a href="tel:+393384071025" className="flex items-center gap-2 footer-link">
                <Phone size={16} />
                338 407 1025
              </a>
              <a href="tel:+393347077175" className="flex items-center gap-2 footer-link">
                <Phone size={16} />
                334 707 7175
              </a>
              <div className="flex items-center gap-2 text-gray-400">
                <MapPin size={16} />
                Via Belfiore 9, 20149 Milano
              </div>
            </div>
          </div>

          {/* Links */}
          <div className="md:text-right">
            <Link to="/privacy" className="block footer-link text-sm mb-4" data-testid="footer-privacy-link">
              Privacy Policy
            </Link>
            <div className="flex gap-3 md:justify-end">
              <a href="#" className="w-8 h-8 rounded-full bg-gray-800 flex items-center justify-center hover:bg-[#E6007E] transition-colors" aria-label="Instagram">
                <Instagram size={16} className="text-gray-400 hover:text-white" />
              </a>
              <a href="#" className="w-8 h-8 rounded-full bg-gray-800 flex items-center justify-center hover:bg-[#E6007E] transition-colors" aria-label="LinkedIn">
                <Linkedin size={16} className="text-gray-400 hover:text-white" />
              </a>
            </div>
          </div>
        </div>

        <div className="mt-10 pt-8 border-t border-gray-800 text-center">
          <p className="text-gray-500 text-xs">
            © {new Date().getFullYear()} Flippit - Tutti i diritti riservati
          </p>
        </div>
      </div>
    </footer>
  );
};

// Mobile Sticky CTA
const MobileStickyCTA = () => {
  const [show, setShow] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setShow(window.scrollY > 500);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToForm = () => {
    document.getElementById('contact-form')?.scrollIntoView({ behavior: 'smooth' });
  };

  if (!show) return null;

  return (
    <div className="mobile-sticky-cta md:hidden" data-testid="mobile-cta">
      <Button 
        onClick={scrollToForm}
        className="w-full btn-magenta py-4 text-base font-semibold rounded-full"
      >
        Valuta il tuo immobile
      </Button>
    </div>
  );
};

// Privacy Page
const PrivacyPage = () => {
  return (
    <div className="min-h-screen bg-white">
      <div className="container-flippit py-20">
        <Link to="/" className="inline-flex items-center gap-2 text-flippit-magenta hover:underline mb-8">
          <ArrowRight className="rotate-180" size={16} />
          Torna alla home
        </Link>
        
        <h1 className="heading-lg text-flippit-dark mb-8">Privacy Policy</h1>
        
        <div className="prose prose-lg max-w-none text-flippit-gray">
          <p className="mb-6">
            <strong>Ultimo aggiornamento:</strong> {new Date().toLocaleDateString('it-IT')}
          </p>

          <h2 className="heading-md text-flippit-dark mt-8 mb-4">1. Titolare del Trattamento</h2>
          <p>
            Il Titolare del trattamento dei dati personali è Flippit S.r.l., con sede in Milano, 
            Via Example 123, 20100 Milano (MI), P.IVA 12345678901.
          </p>

          <h2 className="heading-md text-flippit-dark mt-8 mb-4">2. Dati Raccolti</h2>
          <p>
            Raccogliamo i seguenti dati personali quando compili il form di contatto:
          </p>
          <ul className="list-disc pl-6 mt-4 space-y-2">
            <li>Nome e cognome</li>
            <li>Indirizzo email</li>
            <li>Numero di telefono</li>
            <li>Zona dell'immobile</li>
            <li>Metratura dell'immobile</li>
          </ul>

          <h2 className="heading-md text-flippit-dark mt-8 mb-4">3. Finalità del Trattamento</h2>
          <p>
            I dati raccolti vengono utilizzati esclusivamente per:
          </p>
          <ul className="list-disc pl-6 mt-4 space-y-2">
            <li>Rispondere alle tue richieste di valutazione immobiliare</li>
            <li>Contattarti per fornirti informazioni sui nostri servizi</li>
            <li>Migliorare i nostri servizi</li>
          </ul>

          <h2 className="heading-md text-flippit-dark mt-8 mb-4">4. Base Giuridica</h2>
          <p>
            Il trattamento dei tuoi dati è basato sul tuo consenso espresso al momento dell'invio 
            del form di contatto e sull'esecuzione di misure precontrattuali.
          </p>

          <h2 className="heading-md text-flippit-dark mt-8 mb-4">5. Conservazione dei Dati</h2>
          <p>
            I dati personali saranno conservati per il tempo necessario al perseguimento delle 
            finalità per le quali sono stati raccolti e comunque non oltre 24 mesi dalla raccolta.
          </p>

          <h2 className="heading-md text-flippit-dark mt-8 mb-4">6. Diritti dell'Interessato</h2>
          <p>
            Hai il diritto di:
          </p>
          <ul className="list-disc pl-6 mt-4 space-y-2">
            <li>Accedere ai tuoi dati personali</li>
            <li>Richiedere la rettifica o la cancellazione dei dati</li>
            <li>Richiedere la limitazione del trattamento</li>
            <li>Opporti al trattamento</li>
            <li>Richiedere la portabilità dei dati</li>
            <li>Revocare il consenso in qualsiasi momento</li>
          </ul>

          <h2 className="heading-md text-flippit-dark mt-8 mb-4">7. Contatti</h2>
          <p>
            Per esercitare i tuoi diritti o per qualsiasi domanda relativa alla privacy, 
            puoi contattarci all'indirizzo email: privacy@flippit.it
          </p>

          <h2 className="heading-md text-flippit-dark mt-8 mb-4">8. Modifiche alla Privacy Policy</h2>
          <p>
            Ci riserviamo il diritto di modificare questa Privacy Policy in qualsiasi momento. 
            Le modifiche saranno pubblicate su questa pagina con la data di aggiornamento.
          </p>
        </div>
      </div>
      <Footer />
    </div>
  );
};

// Landing Page Component
const LandingPage = () => {
  useScrollReveal();

  return (
    <>
      <HeroSection />
      <TargetAudienceSection />
      <ValueCreationSection />
      <BenefitsSection />
      <GreenSection />
      <MarketNumbersSection />
      <ContactFormSection />
      <Footer />
    </>
  );
};

// Main App
function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <Toaster position="top-center" richColors />
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
